import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import TrackCard from "../components/TrackCard";
import TaxonomyFilterBar, { filtersToParams } from "../components/TaxonomyFilterBar";
import { useAuth } from "../context/AuthContext";

export default function MusicLibrary() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [filters, setFilters] = useState({});
  const [favIds, setFavIds] = useState([]);
  const { user } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filtersToParams(filters) };
      if (category !== "all") params.category = category;
      if (query.trim()) params.search = query.trim();
      const { data } = await api.get("/tracks", { params });
      setTracks(data || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [category, filters, query]);

  const loadFavs = useCallback(async () => {
    if (!user) { setFavIds([]); return; }
    try {
      const { data } = await api.get("/favorites/ids");
      setFavIds(data || []);
    } catch {}
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFavs(); }, [loadFavs]);

  const toggleFav = async (trackId) => {
    if (!user) return;
    try {
      if (favIds.includes(trackId)) {
        await api.delete(`/favorites/${trackId}`);
        setFavIds(favIds.filter((id) => id !== trackId));
      } else {
        await api.post("/favorites", { track_id: trackId });
        setFavIds([...favIds, trackId]);
      }
    } catch {}
  };

  const filtered = tracks; // Filters now applied server-side via /api/tracks

  const categories = [
    { id: "all", label: "All music" },
    { id: "music", label: "Music" },
    { id: "worship", label: "Worship" },
    { id: "instrumental", label: "Instrumental" },
    { id: "study", label: "Study song" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <div className="overline text-[#C5A059] mb-2">Explore the library</div>
        <h1 className="font-serif text-[#FDFBF7] text-4xl sm:text-5xl tracking-tight">Music</h1>
        <p className="text-[#A1A4AB] mt-2">Songs submitted by brothers and sisters, approved for the station.</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              data-testid={`filter-${c.id}`}
              className={`overline px-4 py-2 rounded-full border transition-all ${category === c.id ? "bg-[#C5A059] text-[#060709] border-[#C5A059]" : "border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <input
          placeholder="Search title, artist, genre..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="input-lux md:max-w-xs"
          data-testid="music-search-input"
        />
      </div>

      <TaxonomyFilterBar value={filters} onChange={setFilters} testIdPrefix="music-tax-filter" />

      {err && <div className="text-red-400 text-sm" data-testid="music-error">{err}</div>}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-lg shimmer" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-16 text-center">
          <div className="text-[#FDFBF7] font-serif text-2xl mb-2">Nothing here yet</div>
          <div className="text-[#A1A4AB]">Be the first to submit music in this category.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6" data-testid="music-grid">
          {filtered.map((t, i) => (
            <TrackCard
              key={t.id}
              track={t}
              list={filtered}
              index={i}
              showFavorite={!!user}
              onFavoriteToggle={toggleFav}
              isFavorited={favIds.includes(t.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
